import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComparisonTable = ({ plans, onSelectPlan }) => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  const allFeatures = [
    'API Requests per Month',
    'Concurrent Connections',
    'Message History',
    'Real-time Messaging',
    'File Attachments',
    'Push Notifications',
    'Webhook Support',
    'Custom Integrations',
    'Analytics Dashboard',
    'Multi-language Support',
    'Priority Support',
    'SLA Guarantee',
    'Custom Branding',
    'Advanced Security',
    'Dedicated Account Manager',
    'Custom Deployment'
  ];

  const displayFeatures = showAllFeatures ? allFeatures : allFeatures.slice(0, 8);

  const getFeatureValue = (plan, featureName) => {
    const featureMap = {
      'API Requests per Month': plan.apiRequests || 'N/A',
      'Concurrent Connections': plan.connections || 'N/A',
      'Message History': plan.history || 'N/A',
      'Real-time Messaging': plan.realtime ? 'Yes' : 'No',
      'File Attachments': plan.attachments ? 'Yes' : 'No',
      'Push Notifications': plan.pushNotifications ? 'Yes' : 'No',
      'Webhook Support': plan.webhooks ? 'Yes' : 'No',
      'Custom Integrations': plan.customIntegrations ? 'Yes' : 'No',
      'Analytics Dashboard': plan.analytics ? 'Yes' : 'No',
      'Multi-language Support': plan.multiLanguage ? 'Yes' : 'No',
      'Priority Support': plan.prioritySupport ? 'Yes' : 'No',
      'SLA Guarantee': plan.sla || 'No',
      'Custom Branding': plan.branding ? 'Yes' : 'No',
      'Advanced Security': plan.security ? 'Yes' : 'No',
      'Dedicated Account Manager': plan.accountManager ? 'Yes' : 'No',
      'Custom Deployment': plan.customDeployment ? 'Yes' : 'No'
    };

    return featureMap[featureName] || 'No';
  };

  const isFeatureIncluded = (plan, featureName) => {
    const value = getFeatureValue(plan, featureName);
    return value !== 'No' && value !== 'N/A';
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="p-6 border-b border-border">
        <h3 className="text-xl font-semibold text-foreground mb-2">Feature Comparison</h3>
        <p className="text-muted-foreground">Compare all features across different plans</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-4 font-medium text-foreground min-w-[200px]">
                Features
              </th>
              {plans.map((plan) => (
                <th key={plan.id} className="text-center p-4 min-w-[150px]">
                  <div className="space-y-2">
                    <div className="font-semibold text-foreground">{plan.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {typeof plan.price === 'string' ? plan.price : `$${plan.price}/mo`}
                    </div>
                    {plan.isPopular && (
                      <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        Popular
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayFeatures.map((feature, index) => (
              <tr key={feature} className={`border-b border-border ${index % 2 === 0 ? 'bg-muted/20' : ''}`}>
                <td className="p-4 font-medium text-foreground">{feature}</td>
                {plans.map((plan) => (
                  <td key={`${plan.id}-${feature}`} className="p-4 text-center">
                    <div className="flex items-center justify-center">
                      {isFeatureIncluded(plan, feature) ? (
                        <div className="flex items-center space-x-2">
                          <Icon name="Check" size={16} className="text-accent" />
                          <span className="text-sm text-foreground">
                            {getFeatureValue(plan, feature) === 'Yes' ? '' : getFeatureValue(plan, feature)}
                          </span>
                        </div>
                      ) : (
                        <Icon name="X" size={16} className="text-muted-foreground" />
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border bg-muted/10">
              <td className="p-4"></td>
              {plans.map((plan) => (
                <td key={`cta-${plan.id}`} className="p-4 text-center">
                  <Button
                    variant={plan.isPopular ? 'default' : 'outline'}
                    size="sm"
                    iconName="ArrowRight"
                    iconPosition="right"
                    onClick={() => onSelectPlan(plan)}
                    className={plan.isPopular ? 'shadow-elevation-1' : ''}
                  >
                    {plan.ctaText || 'Select Plan'}
                  </Button>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="p-4 border-t border-border text-center">
        <Button
          variant="ghost"
          size="sm"
          iconName={showAllFeatures ? "ChevronUp" : "ChevronDown"}
          iconPosition="right"
          onClick={() => setShowAllFeatures(!showAllFeatures)}
          className="text-muted-foreground hover:text-foreground"
        >
          {showAllFeatures ? 'Show Less Features' : 'Show All Features'}
        </Button>
      </div>
    </div>
  );
};

export default ComparisonTable;